"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useActionArea = void 0;
var _slicedToArray2 = _interopRequireDefault(require("@babel/runtime/helpers/slicedToArray"));
var _lodash = require("lodash");
var _react = require("react");
var _browser = require("../../../utils/browser.utils");
var isInsideElementRect = function isInsideElementRect(elementRect, event) {
  return event.clientX >= elementRect.left && event.clientX <= elementRect.right && event.clientY >= elementRect.top && event.clientY <= elementRect.bottom;
};

/**
 * useActionArea is a hook for managing hover, focus, and keyboard interactions on interactive elements.
 *
 * Features:
 * - Handles hover, focus-visible, and keyboard states.
 * - Supports `Space` and `Enter` for triggering `onClick`.
 * - Includes refs and ARIA attributes for accessibility.
 */
var useActionArea = exports.useActionArea = function useActionArea(props) {
  var _ref = props || {},
    _ref$enabled = _ref.enabled,
    enabled = _ref$enabled === void 0 ? true : _ref$enabled,
    id = _ref.id,
    onClick = _ref.onClick,
    onMouseEnter = _ref.onMouseEnter,
    onMouseLeave = _ref.onMouseLeave,
    onFocus = _ref.onFocus,
    onBlur = _ref.onBlur,
    onMouseDown = _ref.onMouseDown,
    onKeyDown = _ref.onKeyDown,
    disabled = _ref.disabled;
  var actionAreaRef = (0, _react.useRef)(null);
  var _useState = (0, _react.useState)(false),
    _useState2 = (0, _slicedToArray2["default"])(_useState, 2),
    isHovered = _useState2[0],
    setIsHovered = _useState2[1];
  var _useState3 = (0, _react.useState)(false),
    _useState4 = (0, _slicedToArray2["default"])(_useState3, 2),
    isFocusVisible = _useState4[0],
    setIsFocusVisible = _useState4[1];
  var _useState5 = (0, _react.useState)(false),
    _useState6 = (0, _slicedToArray2["default"])(_useState5, 2),
    isKeyboardFocusDetection = _useState6[0],
    setIsKeyboardFocusDetection = _useState6[1];
  var handleMouseEnter = (0, _react.useCallback)(function (event) {
    if (disabled) {
      return;
    }
    setIsHovered(true);
    onMouseEnter === null || onMouseEnter === void 0 || onMouseEnter(event);
  }, [disabled, onMouseEnter]);
  var handleMouseLeave = (0, _react.useCallback)(function (event) {
    if (disabled || !actionAreaRef.current) {
      return;
    }
    var elementRect = actionAreaRef.current.getBoundingClientRect();
    if (!isInsideElementRect(elementRect, event)) {
      setIsHovered(false);
      onMouseLeave === null || onMouseLeave === void 0 || onMouseLeave(event);
    }
  }, [disabled, onMouseLeave]);
  var handleMouseDown = (0, _react.useCallback)(function (event) {
    if (disabled) {
      return;
    }
    setIsKeyboardFocusDetection(false);
    // Apply blur only on iOS and macOS and avoid blurring on Windows and Android to prevent losing the focus indicator
    if ((0, _browser.isIOS)() && (0, _browser.isMacOS)()) {
      var _actionAreaRef$curren;
      (_actionAreaRef$curren = actionAreaRef.current) === null || _actionAreaRef$curren === void 0 || _actionAreaRef$curren.blur();
    }
    onMouseDown === null || onMouseDown === void 0 || onMouseDown(event);
  }, [disabled, onMouseDown]);
  var handleFocus = (0, _react.useCallback)(function (event) {
    if (disabled) {
      return;
    }

    // Assume focus is keyboard-driven if no prior mouse interaction
    if (isKeyboardFocusDetection || !isHovered) {
      setIsFocusVisible(true);
    }
    onFocus === null || onFocus === void 0 || onFocus(event);
  }, [disabled, isHovered, isKeyboardFocusDetection, onFocus]);
  var handleBlur = (0, _react.useCallback)(function (event) {
    if (disabled) {
      return;
    }
    setIsFocusVisible(false);
    onBlur === null || onBlur === void 0 || onBlur(event);
  }, [disabled, onBlur]);
  var handleKeyDown = (0, _react.useCallback)(function (event) {
    if (disabled) {
      return;
    }
    if (event.code === 'Space' || event.code === 'Enter') {
      event.preventDefault();
      onClick === null || onClick === void 0 || onClick(event);
    }
    if (event.code === 'Tab') {
      var _actionAreaRef$curren2;
      setIsKeyboardFocusDetection(true);
      (_actionAreaRef$curren2 = actionAreaRef.current) === null || _actionAreaRef$curren2 === void 0 || _actionAreaRef$curren2.focus();
    }
    onKeyDown === null || onKeyDown === void 0 || onKeyDown(event);
  }, [disabled, onClick, onKeyDown]);
  var handleClick = (0, _react.useCallback)(function (event) {
    if (disabled) {
      return;
    }
    onClick === null || onClick === void 0 || onClick(event);
  }, [disabled, onClick]);
  var associatedId = (0, _react.useMemo)(function () {
    return id !== null && id !== void 0 ? id : (0, _lodash.uniqueId)('action-area-');
  }, [id]);
  if (!enabled) {
    return {
      containerProps: {},
      actionAreaProps: {}
    };
  }
  return {
    containerProps: {
      'data-hover': isHovered || null,
      'data-focus-visible': isFocusVisible || null,
      'data-interactive': !disabled || null,
      'data-disabled': disabled || null,
      id: associatedId
    },
    actionAreaProps: {
      ref: actionAreaRef,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      onMouseDown: handleMouseDown,
      onFocus: handleFocus,
      onBlur: handleBlur,
      onKeyDown: handleKeyDown,
      onClick: handleClick,
      'aria-labelledby': associatedId,
      disabled: disabled
    }
  };
};
//# sourceMappingURL=useActionArea.js.map